import { Contract, ethers } from 'ethers'
import { useCallback } from 'react'
import { useCallWithGasPrice } from 'hooks/useCallWithGasPrice'
import { TransactionResponse } from '@ethersproject/providers'
import { DEFAULT_TOKEN_DECIMAL } from 'config'
import getGasPrice from 'utils/getGasPrice'
import { estimateGas } from 'utils/calls/estimateGas'
import BigNumber from 'bignumber.js'
import { useVvsBoost } from 'hooks/useContract'

const options = {
  gasLimit: 1000000,
}

const deposit = async (vvsBoost, pid, amount) => {
  const gasPrice = getGasPrice()
  const formattedAmount = new BigNumber(amount).times(DEFAULT_TOKEN_DECIMAL).toString()
  const estimatedGasLimit = await estimateGas(vvsBoost, 'deposit', [pid, formattedAmount], 1000)
  const tx = await vvsBoost.deposit(pid, formattedAmount, {
    gasLimit: estimatedGasLimit || options.gasLimit,
    gasPrice,
  })
  const receipt = await tx.wait()
  return {
    ...receipt,
    hash: receipt.transactionHash,
  }
}

const batchWithdraw = async (vvsBoost, stakeIds) => {
  const gasPrice = getGasPrice()

  const tx = await vvsBoost.batchWithdraw(
    stakeIds.map((x) => x.toString()),
    {
      gasPrice,
    },
  )
  const receipt = await tx.wait()
  return {
    ...receipt,
    hash: receipt.transactionHash,
  }
}

const batchUpgrade = async (vvsBoost, stakeIds, newPids) => {
  const gasPrice = getGasPrice()

  const tx = await vvsBoost.batchUpgrade(
    stakeIds.map((x) => x.toString()),
    newPids.map((x) => x.toString()),
    {
      gasPrice,
    },
  )
  const receipt = await tx.wait()
  return {
    ...receipt,
    hash: receipt.transactionHash,
  }
}

const useVvsBoostHandlers = () => {
  const vvsBoost = useVvsBoost()
  const { callWithGasPrice } = useCallWithGasPrice()

  const handleApprove = useCallback(
    async (ERC20: Contract) => {
      try {
        const tx = await callWithGasPrice(ERC20, 'approve', [vvsBoost.address, ethers.constants.MaxUint256])
        const receipt = await tx.wait()
        return receipt.status
      } catch (e) {
        return false
      }
    },
    [vvsBoost, callWithGasPrice],
  )

  const handleDeposit = useCallback(
    async (pid: string, amount: string): Promise<TransactionResponse> => {
      return deposit(vvsBoost, pid, amount)
    },
    [vvsBoost],
  )

  const handleBatchWithdraw = useCallback(
    async (stakeIds: number[]): Promise<TransactionResponse> => {
      return batchWithdraw(vvsBoost, stakeIds)
    },
    [vvsBoost],
  )

  const handleBatchUpgrade = useCallback(
    async (stakeIds: number[], newPids: number[]): Promise<TransactionResponse> => {
      return batchUpgrade(vvsBoost, stakeIds, newPids)
    },
    [vvsBoost],
  )

  return {
    onApprove: handleApprove,
    onDeposit: handleDeposit,
    onBatchWithdraw: handleBatchWithdraw,
    onBatchUpgrade: handleBatchUpgrade,
  }
}

export default useVvsBoostHandlers
